import { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn } from "lucide-react";

export default function AdminLogin() {
    const { admin, checking, login } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = "Sign in | Chesterton Admin";
    }, []);

    if (checking) return null;
    if (admin) {
        const dest = location.state?.from?.pathname || "/admin";
        return <Navigate to={dest} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Enter your email and password.");
            return;
        }
        setSubmitting(true);
        const res = await login(email.trim(), password);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Signed in.");
            const dest = location.state?.from?.pathname || "/admin";
            navigate(dest, { replace: true });
        } else {
            toast.error(res.error || "Sign in failed.");
        }
    };

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="admin-login-page">
            <div className="max-w-[420px] w-full">
                <div className="text-center mb-10">
                    <div className="font-serif-display text-4xl !text-acre-ink leading-none">Chesterton</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-acre-muted mt-2">
                        Admin · Sign in
                    </div>
                </div>
                <form
                    onSubmit={onSubmit}
                    data-testid="admin-login-form"
                    className="bg-white border border-acre-line p-8 space-y-5"
                >
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="admin-login-email"
                            className="rounded-none border-acre-line focus-visible:ring-acre-primary"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-login-password"
                            className="rounded-none border-acre-line focus-visible:ring-acre-primary"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="w-full rounded-none bg-acre-primary hover:bg-acre-primaryHover text-white py-6 text-sm tracking-wide"
                    >
                        {submitting ? "Signing in…" : (<><LogIn size={16} className="mr-2" /> Sign In</>)}
                    </Button>
                </form>
                <div className="text-center mt-6">
                    <a href="/" className="text-[11px] uppercase tracking-[0.2em] text-acre-muted hover:text-acre-ink">← Back to site</a>
                </div>
            </div>
        </div>
    );
}
